import { Link } from 'react-router-dom';
import { useDashboard } from '../lib/queries';
import { DEMO_APPOINTMENTS, DEMO_ALERTS } from '../lib/demo-data';
import { resolveInviteUrl } from '../lib/invite-url';
import Spinner from '../components/Spinner';
import type { PracticeContext } from '../lib/types';

export default function Dashboard({ context }: { context: PracticeContext }) {
  const { data, isLoading, error } = useDashboard();
  const p = context.practice;

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="alert alert-danger">
        Erro ao carregar painel: {(error as Error).message}
      </div>
    );
  }

  const stats = data!;
  const todayAppts = DEMO_APPOINTMENTS.filter((a) => a.status === 'proxima');
  const inviteUrl = resolveInviteUrl(p.inviteUrl);

  return (
    <div>
      <div className="mb-4">
        <p className="text-muted small mb-1">Bom dia 👩‍⚕️</p>
        <h1 className="h3 mb-0">
          {p.doctorName ?? p.displayName}
        </h1>
        {p.crm && <p className="text-muted small mb-0">{p.crm}</p>}
      </div>

      {stats.alertsCount > 0 && (
        <Link
          to="/alerts"
          className="card shadow-sm mb-4 text-decoration-none border-danger border-opacity-25"
        >
          <div className="card-body d-flex align-items-center gap-3">
            <div
              className="rounded-3 d-flex align-items-center justify-content-center bg-danger bg-opacity-10"
              style={{ width: 48, height: 48 }}
            >
              <i className="bi bi-bell-fill text-danger fs-4" />
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold text-danger">
                {stats.alertsCount === 1
                  ? '1 alerta ativo'
                  : `${stats.alertsCount} alertas ativos`}
              </div>
              <div className="small text-muted">
                {DEMO_ALERTS[0]?.message ?? 'Ver detalhes'}
              </div>
            </div>
            <i className="bi bi-chevron-right text-muted" />
          </div>
        </Link>
      )}

      <div className="row g-3 mb-4">
        <Stat label="Famílias (waitlist)" value={stats.waitlistTotal} icon="bi-people" />
        <Stat label="Últimos 7 dias" value={stats.waitlistLast7d} icon="bi-calendar-week" tone="info" />
        <Stat label="Pacientes demo" value={stats.demoPatientsCount} icon="bi-emoji-smile" />
        <Stat label="Consultas hoje" value={stats.appointmentsToday} icon="bi-calendar-check" tone="warning" />
      </div>

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="h6 mb-0">Próximas consultas</h2>
                <Link to="/agenda" className="small fw-semibold text-success text-decoration-none">
                  agenda ›
                </Link>
              </div>
              {todayAppts.map((c) => (
                <div
                  key={c.id}
                  className="d-flex align-items-center gap-3 border rounded-3 p-3 mb-2"
                >
                  <div
                    className="rounded-3 text-center bg-success bg-opacity-10 px-2 py-1 flex-shrink-0"
                    style={{ minWidth: 56 }}
                  >
                    <div className="small fw-bold text-success">{c.date}</div>
                    <div className="fw-bold text-success">{c.time}</div>
                  </div>
                  <div>
                    <div className="fw-semibold">{c.baby}</div>
                    <div className="small text-muted">{c.type}</div>
                  </div>
                </div>
              ))}
              <p className="small text-muted mb-0">
                Dados de demonstração até integrar agenda com o app.
              </p>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h2 className="h6 mb-3">Convite do consultório</h2>
              <p className="small text-muted">
                Compartilhe com as famílias. Inscrições entram na waitlist já
                vinculadas ao seu slug <code>{p.slug}</code>.
              </p>
              <div className="input-group input-group-sm mb-3">
                <input
                  className="form-control"
                  readOnly
                  value={inviteUrl}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    void navigator.clipboard.writeText(inviteUrl);
                  }}
                >
                  Copiar
                </button>
              </div>
              <Link to="/patients" className="btn btn-sm btn-bb">
                Ver famílias e pacientes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: string;
  tone?: 'info' | 'warning';
}) {
  const toneClass =
    tone === 'info' ? 'text-info' : tone === 'warning' ? 'text-warning' : 'text-success';
  return (
    <div className="col-6 col-lg-3">
      <div className="card shadow-sm h-100">
        <div className="card-body">
          <div className="d-flex justify-content-between">
            <span className="small text-muted">{label}</span>
            <i className={`bi ${icon} ${toneClass}`} />
          </div>
          <div className="display-6 fw-semibold mt-1">{value}</div>
        </div>
      </div>
    </div>
  );
}
